import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'Products', path: '/products' },
  { label: 'About', path: '/about' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const styles = {
    nav: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      background: '#C0392B',
      boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.25)' : 'none',
      transition: 'box-shadow 0.3s ease',
    },
    inner: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 24px',
      height: '68px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    logo: {
      display: 'flex',
      flexDirection: 'column',
      lineHeight: 1,
      textDecoration: 'none',
    },
    logoTop: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 800,
      fontSize: '18px',
      color: '#FFFFFF',
      letterSpacing: '0.04em',
    },
    logoBottom: {
      fontFamily: 'Poppins, sans-serif',
      fontWeight: 600,
      fontSize: '11px',
      color: '#E67E22',
      letterSpacing: '0.06em',
      marginTop: '1px',
    },
    desktopLinks: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
    },
    link: (active) => ({
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500,
      fontSize: '15px',
      color: active ? '#E67E22' : '#FFFFFF',
      padding: '8px 14px',
      borderRadius: '6px',
      transition: 'background 0.2s, color 0.2s',
      background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
    }),
    cta: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      background: '#E67E22',
      color: '#1A1A1A',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      fontSize: '14px',
      padding: '10px 20px',
      borderRadius: '100px',
      textDecoration: 'none',
      transition: 'background 0.2s, transform 0.2s',
      whiteSpace: 'nowrap',
    },
    hamburger: {
      display: 'none',
      flexDirection: 'column',
      gap: '5px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '6px',
    },
    bar: {
      width: '24px',
      height: '2px',
      background: '#FFFFFF',
      borderRadius: '2px',
      transition: 'transform 0.3s, opacity 0.3s',
    },
    mobileMenu: {
      background: '#C0392B',
      borderTop: '1px solid rgba(255,255,255,0.15)',
      padding: '12px 24px 20px',
    },
    mobileLink: (active) => ({
      display: 'block',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 500,
      fontSize: '16px',
      color: active ? '#E67E22' : '#FFFFFF',
      padding: '12px 0',
      borderBottom: '1px solid rgba(255,255,255,0.1)',
    }),
    mobileCta: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      marginTop: '16px',
      background: '#E67E22',
      color: '#1A1A1A',
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      fontSize: '14px',
      padding: '12px 24px',
      borderRadius: '100px',
      textDecoration: 'none',
    },
  };

  return (
    <nav style={styles.nav} role="navigation" aria-label="Main navigation">
      <div style={styles.inner}>
        <Link to="/" style={styles.logo} aria-label="Whanganui Spices and Veges home">
          <span style={styles.logoTop}>WHANGANUI</span>
          <span style={styles.logoBottom}>SPICES AND VEGES</span>
        </Link>

        <div style={styles.desktopLinks} className="desktop-nav">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              style={styles.link(location.pathname === link.path)}
              onMouseEnter={(e) => {
                if (location.pathname !== link.path) {
                  e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                }
              }}
              onMouseLeave={(e) => {
                if (location.pathname !== link.path) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <Link
          to="/contact"
          style={styles.cta}
          className="desktop-nav"
          onMouseEnter={(e) => { e.currentTarget.style.background = '#d4711e'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#E67E22'; }}
          aria-label="Visit us"
        >
          Visit Us <span aria-hidden="true">&#8594;</span>
        </Link>

        <button
          style={styles.hamburger}
          className="hamburger-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-expanded={menuOpen}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
        >
          <span style={{ ...styles.bar, transform: menuOpen ? 'translateY(7px) rotate(45deg)' : 'none' }} />
          <span style={{ ...styles.bar, opacity: menuOpen ? 0 : 1 }} />
          <span style={{ ...styles.bar, transform: menuOpen ? 'translateY(-7px) rotate(-45deg)' : 'none' }} />
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: 'easeInOut' }}
            style={{ overflow: 'hidden' }}
            className="mobile-menu-wrapper"
          >
            <div style={styles.mobileMenu}>
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  style={styles.mobileLink(location.pathname === link.path)}
                >
                  {link.label}
                </Link>
              ))}
              <Link to="/contact" style={styles.mobileCta}>
                Visit Us &#8594;
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .hamburger-btn { display: flex !important; }
        }
      `}</style>
    </nav>
  );
}
